import React, { FC, ReactNode } from 'react';
import { BasicButton, GhostButton } from '../../../../../widgets/buttons';
import { Icon } from '../../../../../widgets/icon';
import { SelectedIcon } from '../../../../../widgets/icons/scene';
import { Input, SearchInput } from '../../../../../widgets/input';
import { thinScrollbar } from '../../../../../widgets/scrollbar';
import { ZTabs } from '../../../../../widgets/ztabs';
import styled, { css } from 'styled-components';

export const ComposerPanel = styled.section`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    color: var(--composer-fg, #263238);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line-soft, #eef1f3);
    border-radius: 16px;
    box-shadow: var(--composer-shadow, rgba(38, 50, 56, 0.04) 0 1px 2px);
`;

export const ComposerPanelHeader = styled.header`
    display: flex;
    min-height: 38px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 8px;
    border-bottom: 1px solid var(--composer-line-soft, #eaeff4);
`;

export const ComposerPanelTitle = styled.div`
    min-width: 0;
`;

export const ComposerEyebrow = styled.div`
    overflow: hidden;
    color: var(--composer-faint, #677078);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 16px;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const ComposerTitle = styled.div`
    overflow: hidden;
    color: var(--composer-fg, #1e2a38);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ComposerToolbar = styled.div`
    display: flex;
    min-height: 44px;
    flex: 0 0 auto;
    align-items: center;
    flex-wrap: wrap;
    gap: 3px;
    padding: 6px 8px;
    background: var(--composer-panel, #fff);
    border-bottom: 1px solid var(--composer-line, #dbe2e6);
`;

/* The Scene Editor add button: 32px tall, a 12px label and a glyph a shade larger than the text. */
export const ComposerAddButton = styled(BasicButton)`
    min-width: 0;
    height: 32px;
    padding: 0 9px;
    border-radius: 6px;
    gap: 4px;
    font-size: 12px;

    > svg {
        width: 17px;
        height: 17px;
    }
`;

export const ComposerToolbarSpacer = styled.span`
    flex: 1;
`;

/* Only the pressed state is the Composer's own: the resting colour and the hover are GhostButton's. */
export const ComposerIconButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })<{ $active?: boolean; $danger?: boolean }>`
    width: 30px;
    min-width: 30px;
    height: 32px;
    min-height: 32px;
    border-radius: 6px;

    ${(props) => props.$danger && `color: ${props.theme.app.component.textButton.destructive.default.color};`}

    ${(props) =>
        props.$active &&
        css`
            color: var(--composer-accent-text, #a8480b);
            background: var(--composer-accent-wash, #fdf0e6);
        `}

    ${Icon},
    > svg {
        width: 17px;
        height: 17px;
    }
`;

/* Every row command slot is the shared ghost button at row density: the resting colour, the hover
   and the danger colour are the component's own, and only the pressed wash is the Composer's. */
export const ComposerRowActionButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })<{
    $active?: boolean;
    $danger?: boolean;
    $off?: boolean;
}>`
    width: 28px;
    min-width: 28px;
    height: 28px;
    min-height: 28px;
    border-radius: 5px;

    ${(props) =>
        props.$danger &&
        css`
            color: ${props.theme.app.component.textButton.destructive.default.color};

            &:hover:not([disabled]),
            &:active:not([disabled]) {
                color: ${props.theme.app.component.textButton.destructive.hover.color};
            }
        `}

    ${(props) =>
        (props.$active || props.$off) &&
        css`
            color: var(--composer-accent-text, #a8480b);

            &:hover:not([disabled]),
            &:active:not([disabled]) {
                color: var(--composer-accent-text, #a8480b);
            }
        `}

    ${Icon},
    > svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }
`;

/* SearchInput carries the magnifier and hands this class to the field it wraps, not to its own
   container: the container is already a full-width block, so the row needs no flex rules of its own. */
export const ComposerSearch = styled(SearchInput)`
    height: 32px;
`;

export const ComposerSearchRow = styled.div`
    flex: 0 0 auto;
    padding: 8px 10px;
    border-bottom: 1px solid var(--composer-line-soft, #eef1f3);
`;

export const ComposerTreeToolsRow = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
    padding: 4px 8px;
    border-bottom: 1px solid var(--composer-line-soft, #eaeff4);
`;

export const ComposerLinkButton = styled(GhostButton).attrs({ $size: 'small' })`
    width: auto;
    min-width: 0;
    height: auto;
    min-height: 26px;
    padding-inline: 6px;
    border-radius: 6px;
    color: var(--composer-muted, #5c6b7a);
    font-size: 11.5px;

    &:hover:not([disabled]) {
        background-color: transparent;
        color: var(--composer-fg, #1e2a38);
    }

    &:active:not([disabled]) {
        background-color: transparent;
        color: var(--composer-fg, #1e2a38);
    }

    > svg {
        width: 14px;
        height: 14px;
        flex: 0 0 14px;
    }
`;

export const ComposerScrollBody = styled.div`
    min-width: 0;
    min-height: 0;
    flex: 1;
    overflow: auto;
    overscroll-behavior: contain;
    padding: 8px 6px;
    ${thinScrollbar}
`;

export const ComposerList = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
`;

export const ComposerSubjectHeader = styled.div`
    display: grid;
    min-height: 56px;
    flex: 0 0 auto;
    grid-template-columns: 34px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-bottom: 1px solid var(--composer-line, #dbe2e6);
`;

export const ComposerSubjectMark = styled.div<{ $kind?: 'group' | 'attribute' | 'option' | 'neutral' }>`
    display: flex;
    width: 34px;
    height: 34px;
    align-items: center;
    justify-content: center;
    color: ${(props) => {
        if (props.$kind === 'group') return 'var(--composer-kind-group, #7040d0)';
        if (props.$kind === 'option') return 'var(--composer-kind-option, #b32d7d)';
        if (props.$kind === 'attribute') return 'var(--composer-kind-attribute, #0e7c7c)';
        return 'var(--composer-muted, #5c6b7a)';
    }};
    background: var(--composer-sunken, #f9f9f9);
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dbe2e6);

    ${Icon},
    > svg,
    > img {
        width: 18px;
        height: 18px;
        object-fit: cover;
    }

    /* Same as the tree row icons: the glyph takes the kind colour set above. */
    svg {
        fill: currentColor;
    }
`;

export const ComposerSubjectContent = styled.div`
    min-width: 0;
`;

export const ComposerSubjectName = styled.div`
    overflow: hidden;
    color: var(--composer-fg, #263238);
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/* The inspector name field: plain text until it is pointed at, the way the Scene Editor writes names. */
export const ComposerSubjectNameInput = styled(Input)`
    width: 100%;
    /* No declared height: the field is as tall as its own text plus padding, exactly like the
       inspector name field, so the two read as one control. */
    height: auto;
    padding: 3px 6px;
    margin-left: -6px;
    color: var(--composer-fg, #263238);
    background: transparent;
    border: 1px solid transparent;
    border-radius: 5px;
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;

    &:hover:not(:focus):not(:disabled) {
        background: var(--composer-hover, #eceff1);
        border-color: var(--composer-line, #dbe2e6);
    }

    &:focus {
        background: var(--composer-hover, #eceff1);
        border-color: var(--composer-accent, #e8650a);
    }
`;

export const ComposerSubjectMeta = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    color: var(--composer-faint, #78909c);
    font-size: 9px;
`;

export const ComposerKindBadge = styled.span<{ $kind?: 'group' | 'attribute' | 'option' | 'neutral' }>`
    display: inline-flex;
    height: 15px;
    align-items: center;
    padding: 0 6px;
    color: ${(props) => {
        if (props.$kind === 'group') return 'var(--composer-kind-group, #7040d0)';
        if (props.$kind === 'option') return 'var(--composer-kind-option, #b32d7d)';
        if (props.$kind === 'attribute') return 'var(--composer-kind-attribute, #0e7c7c)';
        return 'var(--composer-muted, #455a64)';
    }};
    /* A filled tint, like the inspector badges: the kind is read by colour, not by an outline. */
    background: ${(props) => {
        if (props.$kind === 'group') return '#dfcff9';
        if (props.$kind === 'option') return '#f4cadf';
        if (props.$kind === 'attribute') return '#c4e4e4';
        return 'var(--composer-sunken, #f9f9f9)';
    }};
    border-radius: 999px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: uppercase;
`;

export const ComposerTabs = styled(ZTabs)`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1;
    flex-direction: column;

    .react-tabs__tab-list {
        width: 100%;
        min-height: 38px;
        margin: 0;
        padding: 6px 8px;
        overflow-x: auto;
        background: var(--composer-panel, #fff);
        border-bottom: 1px solid var(--composer-line, #dbe2e6);
        border-radius: 0;
    }

    .react-tabs__tab {
        flex: 0 0 auto;
        height: 26px;
        padding: 0 8px;
        color: var(--composer-muted, #455a64);
        background: transparent;
        border-radius: 6px;
        box-shadow: none;
        font-size: 12px;
        font-weight: 500;
    }

    .react-tabs__tab:hover {
        color: var(--composer-fg, #1e2a38);
        background: var(--composer-hover, #e9eff5);
    }

    .react-tabs__tab--selected,
    .react-tabs__tab--selected:hover {
        color: var(--composer-accent-text, #a8480b);
        background: rgb(0 0 0 / 6%);
        box-shadow: none;
    }

    .react-tabs__tab-panel {
        min-width: 0;
        min-height: 0;
        flex: 1;
        overflow: hidden;
    }

    .react-tabs__tab-panel--selected {
        display: flex;
        flex-direction: column;
    }
`;

export const ComposerTabCount = styled.span`
    display: inline-flex;
    min-width: 16px;
    height: 16px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    color: var(--composer-faint, #677078);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 999px;
    font-size: 10px;
`;

/* An answer, not a blank panel: the glyph says what the column is for and the line says what to do
   next. Same shape as the Scene Editor inspector, so the two stanze rispondono allo stesso modo. */
const EmptyStateBox = styled.div`
    display: flex;
    height: 100%;
    min-height: 180px;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px 28px;
    color: var(--composer-muted, #455a64);
    text-align: center;

    p {
        max-width: 34ch;
        margin: 0;
        font-size: 13px;
        line-height: 1.6;
    }

    svg {
        width: 26px;
        height: 26px;
        opacity: 0.5;
    }
`;

export const ComposerEmptyState: FC<{ children?: ReactNode; icon?: ReactNode }> = ({ children, icon }) => (
    <EmptyStateBox>
        {icon ?? <SelectedIcon aria-hidden='true' />}
        <p>{children}</p>
    </EmptyStateBox>
);

export const ComposerDetailsStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const ComposerFormStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const ComposerDetailCard = styled.div`
    padding: 12px;
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
`;

export const ComposerDetailLabel = styled.div`
    margin-bottom: 4px;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
`;

export const ComposerDetailValue = styled.div`
    overflow-wrap: anywhere;
    color: var(--composer-fg, #1e2a38);
    font-size: 13px;
    line-height: 19px;
`;

// Settings tabs sit on the sunken tone so their white sections read as separate groups.
export const ComposerSettingsBody = styled(ComposerScrollBody)`
    padding: 10px;
    background: var(--composer-sunken, #f4f7fa);
`;

/* The Composer forms keep the shared Input, TextArea and AdvancedSelect at their own size: only
   the label above them is written smaller here, the way the inspector labels its rows. */
export const composerFieldDensity = css`
    label {
        margin-bottom: 4px;
        color: var(--composer-muted, #455a64);
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
    }
`;

/* The section title stands above its card, the way the inspector writes SCENE or SHADING over
   the cards that follow it. */
export const ComposerFormGroup = styled.section`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const ComposerFormSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dbe2e6);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    ${composerFieldDensity}
`;

export const ComposerFormSectionTitle = styled.h3`
    margin: 0;
    padding: 0 2px;
    color: var(--composer-muted, #455a64);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

export const ComposerRadioRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px 20px;
`;

export const ComposerUnsupportedNote = styled.div`
    padding: 10px 12px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    font-size: 12px;
    line-height: 18px;
`;

// Like the Scene Editor seams: nothing at rest, a short accent pill once the pointer is on it.
export const ComposerResizeHandle = styled.div`
    position: relative;
    width: 6px;
    min-width: 6px;
    height: 100%;
    cursor: col-resize;
    background: var(--composer-app, #f0f0f0);

    &::after {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 4px;
        height: 34px;
        background: var(--composer-accent, #e8650a);
        border-radius: 999px;
        opacity: 0;
        transform: translate(-50%, -50%);
        transition: opacity 130ms ease;
        content: '';
    }

    &:hover::after,
    &:active::after,
    &:focus-visible::after {
        opacity: 1;
    }

    &:focus-visible {
        outline: none;
    }
`;
